import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { HttpStatusCode } from "axios";

import { MemberInfo } from "../../types/member";

import { getLocalValue } from "../../libs/localStorage";
import { formatPhoneNumber } from "../../libs/numberFormat";
import { getBase64FileList } from "../../libs/common";

import { useUserContext } from "../../contexts/userContext";

import { BannerItem, getBanner } from "../../services/bannerServices";
import { logout } from "../../services/authServices";
import {
  getMemberInfo,
  updateMemberProfile,
} from "../../services/memberServices";

import useFullScreenLoading from "../../hooks/useFullScreenLoading";
import useSimplePopup from "../../hooks/useSimplePopup";

import CommonButton from "../../components/Button";
import StickyHeader from "../../components/Layout/StickyHeader";
import CommonPopup from "../../components/Popup";
import HorizontalRule from "../../components/Rule/Horizontal";
import VerticalRule from "../../components/Rule/Vertical";
import CommonCheckbox from "../../components/Input/Checkbox";
import MyInfoChangeCompany from "../../components/MyInfo/ChangeCompany";
import ChangeNicknamePopup from "../../components/Popup/ChangeNickname";
import WorkSpaceYetPopup from "../../components/Popup/WorkSpaceYet";
import CommonProfile from "../../components/MyInfo/Profile";
import ImageUploadPopup from "../../components/Popup/ImageUpload";
import DefaultBanner from "../../components/Banner";

type Props = {};

// eslint-disable-next-line no-empty-pattern
export default function MyInfo(props: Props) {
  const navigate = useNavigate();
  const { state } = useLocation();
  const [, { updateField }] = useUserContext();
  const [popup, setPopup] = useState<string>(state?.popup ?? "");
  const [bannerList, setBannerList] = useState<BannerItem[]>([]);
  const bioToken = getLocalValue("BIO");
  const [memberInfo, setMemberInfo] = useState<MemberInfo>();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const loadingComponent = useFullScreenLoading({ isLoading });
  const [user] = useUserContext();
  const location = useLocation();
  const openLocationError = useRef(false);
  const [simplePopupComponent, { open }] = useSimplePopup({});

  const handleImageUpload = async (base64StringList?: string[]) => {
    if (!base64StringList || base64StringList.length <= 0) {
      setIsLoading(false);
      return;
    }

    const formData = new FormData();

    if (base64StringList && base64StringList.length > 0) {
      const fileList = await getBase64FileList(base64StringList, "profile");
      formData.append("uploadFiles", fileList[0]);
    }

    try {
      await updateMemberProfile(formData);
      await loadMemberInfo();
    } catch (e: any) {
      open("프로필 사진 변경에 실패했습니다.");
    } finally {
      setIsLoading(false);
      setPopup("");
    }
  };

  const loadBannerList = async () => {
    try {
      const res = await getBanner({ location: "MYINFO" });
      if (res.status === HttpStatusCode.Ok) {
        await setBannerList(res.data.items);
      }
    } catch (e: any) {
      setBannerList([]);
    }
  };

  const loadMemberInfo = async () => {
    try {
      const res = await getMemberInfo();
      if (res.status === HttpStatusCode.Ok) {
        await setMemberInfo(res.data);
        updateField({ ...res.data });
      } else {
      }
    } catch (e: any) {
      if (e?.response?.status === HttpStatusCode.Unauthorized) {
        logout();
      } else if (e?.response?.data?.message) {
        open?.(e?.response?.data?.message.replace(". ", ".\n"));
      } else {
        open?.("내정보 조회에 실패하였습니다.");
      }
    }
  };

  useEffect(() => {
    if (openLocationError.current === false && location?.state?.error) {
      open(location.state.error);
      openLocationError.current = true;
    }
  }, [location.state]);

  useEffect(() => {
    if (user.isLoading) return;
    Promise.allSettled([loadMemberInfo(), loadBannerList()]).finally(() => {
      setIsLoading(false);
    });
  }, [user.isLoading]);

  return (
    <div className="px-5 pb-4">
      {loadingComponent}

      <StickyHeader
        title="내 정보"
        onBackClick={() => navigate("/view")}
        rightContent={
          <div onClick={() => navigate("config")}>
            <MyInfoConfigIcon />
          </div>
        }
      />

      <div className="flex justify-between items-center pt-1 pb-8">
        <div className=" whitespace-pre-line text-3xl font-light leading-[38px] tracking-[-1.2px]">
          <strong className="font-medium">{memberInfo?.nickname}</strong>
          {"님,\n안녕하세요"}
        </div>
        <div className="bg-brand-floating-primary-button min-w-[100px] h-[100px] rounded-full flex items-center justify-center relative">
          <CommonProfile
            sizeVariant="myinfo"
            src={memberInfo?.profileImage?.filePath}
          />
          <div
            className="absolute bg-brand-floating-primary-button rounded-full p-1 right-0 bottom-0"
            onClick={() => setPopup("imageUpload-profile")}
          >
            <MyProfileCamera />
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-5">
        <div>
          <HorizontalRule margin="0" className="bg-black border-[1.5px]" />
          <div className="flex h-bottom-button text-sm font-medium leading-[17px]">
            <div
              className="flex-1 flex gap-1 items-center justify-center"
              onClick={() => navigate("/history")}
            >
              <MyProfileHistory />
              히스토리
            </div>
            <VerticalRule className="!h-bottom-button bg-opacity-30" />
            <div
              className="flex-1 flex gap-1 items-center justify-center"
              onClick={() => navigate("/alarm")}
            >
              <MyProfileAlertList />
              알림내역
            </div>
          </div>
          <HorizontalRule margin="0" className="bg-black" />
          {bannerList.length > 0 && (
            <DefaultBanner
              position="MYINFO"
              item={bannerList[0]}
              className="pt-5"
              imageClassName="w-full object-contain"
            />
          )}
        </div>

        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-5">
            <LabelWithChildren label={"아이디(이메일)"}>
              {memberInfo?.email}
            </LabelWithChildren>

            <div className="flex gap-1.5">
              <CommonButton
                variant="tertiary-s"
                className="flex-1"
                onClick={() => navigate("change/email")}
              >
                이메일 변경하기
              </CommonButton>
              <CommonButton
                variant="tertiary-s"
                className="flex-1"
                onClick={() => navigate("change/password")}
              >
                비밀번호 변경하기
              </CommonButton>
            </div>

            <HorizontalRule margin="0" />

            <LabelWithChildren label={"간편 로그인"}>
              <div className="flex flex-col">
                {/* <div
                  className="flex justify-between items-center py-3 opacity-30"
                  // TODO: 10월 오픈에도 미사용이라 가려둠
                  // onClick={() => navigate("config/simple/login")}
                >
                  <div className="">
                    {memberInfo?.isJoinWithMyLgId ? (
                      <>
                        <span className="text-brand-primary-red">my LG ID</span>{" "}
                        연결 중
                      </>
                    ) : (
                      <div>간편 로그인 (추후 지원)</div>
                    )}
                  </div>
                  <MyProfileRightArrow />
                </div>
                <HorizontalRule margin="0px" /> */}
                <div
                  className="flex justify-between items-center"
                  onClick={() => navigate("config/biometric/authentication")}
                >
                  {bioToken ? (
                    <div>
                      <span className="text-brand-primary-red">생체 인증</span>{" "}
                      로그인 설정됨
                    </div>
                  ) : (
                    <div>생체 인증 미사용</div>
                  )}
                  <MyProfileRightArrow />
                </div>
              </div>
            </LabelWithChildren>

            <HorizontalRule margin="0" />

            <div className="flex justify-between items-center gap-2.5">
              <LabelWithChildren label={"이름 / 휴대전화"}>
                {memberInfo?.name} /{" "}
                {formatPhoneNumber(memberInfo?.mobileNumber ?? "")}
              </LabelWithChildren>
              <CommonButton
                variant="tertiary-s"
                className="min-w-[90px] whitespace-nowrap"
                onClick={() => navigate("/myinfo/cert/identity")}
              >
                다시 인증하기
              </CommonButton>
            </div>

            <HorizontalRule margin="0" />

            <div
              className="flex justify-between items-center"
              onClick={() => setPopup("changeNickname")}
            >
              <LabelWithChildren label={"별명"}>
                {memberInfo?.nickname ?? "별명을 만들어보세요"}
              </LabelWithChildren>
              <MyProfileRightArrow />
            </div>

            <HorizontalRule margin="0" />

            <div
              className="flex justify-between items-center"
              onClick={() => navigate("change/building")}
            >
              <LabelWithChildren label={"근무위치"}>
                {memberInfo?.buildingId && memberInfo.buildingFloorId
                  ? `${memberInfo?.buildingName} ${
                      memberInfo?.buildingFloorName
                    }${
                      memberInfo?.buildingSpaceName
                        ? ` - ${memberInfo?.buildingSpaceName}`
                        : ""
                    }`
                  : "위치를 선택해주세요"}
              </LabelWithChildren>
              <MyProfileRightArrow />
            </div>

            <HorizontalRule margin="0" />

            <div
              className="flex justify-between items-center"
              onClick={() => {
                if (memberInfo?.buildingId && memberInfo.buildingFloorId) {
                  setPopup("changeCompany");
                } else {
                  setPopup("workSpaceYet");
                }
              }}
            >
              <LabelWithChildren label={"회사"}>
                {memberInfo?.companyName ?? "회사를 선택해주세요"}
              </LabelWithChildren>
              <div className="flex items-center gap-4">
                {memberInfo?.companyId ? (
                  <>
                    {!memberInfo?.isCompanyCertificated ? (
                      <CommonButton
                        variant="tertiary-s"
                        onClick={() => navigate("/myinfo/cert/company")}
                        className="whitespace-nowrap"
                      >
                        회사 인증하기
                      </CommonButton>
                    ) : (
                      <CommonCheckbox checked />
                    )}
                  </>
                ) : null}
                <MyProfileRightArrow />
              </div>
            </div>

            <HorizontalRule margin="0" />

            <div
              className="flex justify-between items-center"
              onClick={() => navigate("faq")}
            >
              <LabelWithChildren label={"자주하는 질문"}>
                샌디 FAQ
              </LabelWithChildren>
              <MyProfileRightArrow />
            </div>
          </div>
        </div>
      </div>

      <ImageUploadPopup
        popup={popup}
        onClose={() => setPopup("")}
        onChangeImage={handleImageUpload}
        isProfile
        maxCount={1}
        onCall={() => {
          setIsLoading(true);
        }}
        title="프로필 사진 변경"
      />
      <ChangeNicknamePopup
        popup={popup}
        onClose={(status) => {
          if (status === -1) setPopup("");
          else if (status === HttpStatusCode.Ok) {
            open("별명이 변경되었습니다.", {
              onClick: () => {
                setPopup("");
              },
            });
          } else {
            open("별명이 변경에 실패하였습니다.");
          }
        }}
        memberInfo={memberInfo}
        onLoad={loadMemberInfo}
      />
      <WorkSpaceYetPopup popup={popup} onClose={() => setPopup("")} />

      <CommonPopup
        isOpen={popup === "changeCompany"}
        title="회사 변경"
        padding={0}
        onClose={() => setPopup("")}
        unUseHorizonRule
      >
        <MyInfoChangeCompany
          memberInfo={memberInfo}
          onClose={() => setPopup("")}
          onLoad={loadMemberInfo}
        />
      </CommonPopup>
      {simplePopupComponent}
    </div>
  );
}

export type LabelWithChildrenProps = {
  label?: string;
  children: React.ReactNode;
};

export const LabelWithChildren = ({
  label,
  children,
}: LabelWithChildrenProps) => {
  return (
    <div className="flex flex-col gap-1.5">
      <div className="text-xs font-normal leading-4 text-brand-neutral-60">
        {label}
      </div>
      <div className="text-base font-semibold leading-[19px]">{children}</div>
    </div>
  );
};

export const MyInfoConfigIcon = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M12.2502 15.6876C14.2867 15.6876 15.9377 14.0366 15.9377 12C15.9377 9.96347 14.2867 8.3125 12.2502 8.3125C10.2136 8.3125 8.56262 9.96347 8.56262 12C8.56262 14.0366 10.2136 15.6876 12.2502 15.6876Z"
      stroke="black"
      strokeWidth="1.75"
      strokeLinecap="square"
    />
    <path
      d="M19.0538 18.7156L18.6159 15.6753L21.4677 14.5345V9.46567L18.6169 8.32522L19.0549 5.28442L14.6652 2.75L12.2506 4.64979L9.8361 2.75L5.44636 5.28442L5.88427 8.32471L3.03223 9.46567V14.5345L5.88527 15.6759L5.44744 18.7156L9.83718 21.25L12.2506 19.3511L14.6641 21.25L19.0538 18.7156Z"
      stroke="black"
      strokeWidth="1.75"
      strokeLinecap="square"
    />
  </svg>
);

export const MyProfileKakaoIcon = () => (
  <svg
    width="27"
    height="27"
    viewBox="0 0 27 27"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M13.4907 1.99512C6.29943 1.99512 0.5 6.63461 0.5 12.267C0.5 15.923 2.91256 19.1243 6.53141 20.9522L5.30657 25.5269C5.28347 25.5954 5.27992 25.669 5.29631 25.7395C5.3127 25.8099 5.34838 25.8745 5.39936 25.9258C5.47369 25.9914 5.56934 26.0276 5.66845 26.0279C5.75063 26.0213 5.8286 25.9888 5.89115 25.9351L11.1617 22.3812C11.9397 22.4886 12.7239 22.5444 13.5093 22.5482C20.6913 22.5482 26.5 17.9087 26.5 12.267C26.5 6.62533 20.6727 1.99512 13.4907 1.99512Z"
      fill="#392020"
    />
    <path
      d="M5.04627 10.6434H3.62657C3.45061 10.6485 3.27682 10.6034 3.1255 10.5135C3.05832 10.4714 3.00056 10.4158 2.95585 10.3504C2.91114 10.2849 2.88044 10.2109 2.86569 10.133C2.86118 10.0836 2.86118 10.034 2.86569 9.98462C2.86128 9.89221 2.87921 9.80005 2.91798 9.71606C2.95675 9.63206 3.0152 9.55868 3.08838 9.50209C3.25015 9.38493 3.44541 9.32304 3.64513 9.32573H7.92279C8.10015 9.3195 8.27512 9.36812 8.42386 9.46494C8.49262 9.50517 8.5516 9.56022 8.59653 9.62601C8.64146 9.6918 8.67122 9.76672 8.68367 9.84541C8.6882 9.8917 8.6882 9.93834 8.68367 9.98462C8.68805 10.0784 8.67015 10.1718 8.63144 10.2573C8.59273 10.3427 8.53431 10.4178 8.46097 10.4763C8.30314 10.5933 8.10978 10.6524 7.9135 10.6434H6.5402V15.5242C6.54578 15.6282 6.52969 15.7321 6.49295 15.8296C6.45621 15.927 6.39962 16.0158 6.32678 16.0902C6.25693 16.1614 6.17297 16.2171 6.08028 16.2539C5.98759 16.2907 5.88823 16.3076 5.78859 16.3036C5.61433 16.3086 5.44376 16.2529 5.30608 16.1459C5.17501 16.0415 5.08605 15.8931 5.05555 15.7283C5.04638 15.6606 5.04638 15.5919 5.05555 15.5242L5.04627 10.6434Z"
      fill="#FEE500"
    />
    <path
      d="M9.0934 9.68749C9.13421 9.53479 9.23006 9.40257 9.36249 9.3163C9.5036 9.23632 9.6644 9.19773 9.82645 9.20496H10.179C10.351 9.1988 10.5213 9.24056 10.6708 9.32559C10.8293 9.43218 10.942 9.59447 10.9863 9.78026L12.8421 15.023C12.8903 15.1597 12.9305 15.2992 12.9628 15.4406C12.9659 15.4993 12.9659 15.5581 12.9628 15.6168C12.9659 15.7073 12.9493 15.7973 12.914 15.8806C12.8788 15.964 12.8257 16.0386 12.7586 16.0994C12.6936 16.166 12.6156 16.2185 12.5294 16.2536C12.4432 16.2888 12.3506 16.3057 12.2576 16.3035C12.1144 16.3177 11.9707 16.2834 11.8495 16.206C11.7283 16.1285 11.6367 16.0125 11.5895 15.8767L11.1997 14.7354H8.75935L8.36963 15.8767C8.32353 16.0152 8.23083 16.1335 8.10731 16.2113C7.98379 16.2891 7.83711 16.3217 7.69225 16.3035C7.53534 16.3076 7.38165 16.2585 7.25613 16.1643C7.13385 16.067 7.05134 15.9285 7.02416 15.7746C7.01923 15.7222 7.01923 15.6693 7.02416 15.6168C7.00822 15.5247 7.00822 15.4306 7.02416 15.3385C7.02416 15.2365 7.08911 15.125 7.12623 15.023L9.0934 9.68749ZM10.0213 10.9031L9.13979 13.6868H10.8935L10.0213 10.9031Z"
      fill="#FEE500"
    />
    <path
      d="M13.1751 9.9844C13.1657 9.77616 13.2391 9.57267 13.3793 9.41839C13.4505 9.34651 13.5361 9.29034 13.6304 9.25358C13.7247 9.21682 13.8256 9.20019 13.9267 9.20487C14.1006 9.20285 14.2702 9.25834 14.4092 9.36266C14.5381 9.4715 14.6237 9.62291 14.6505 9.78946C14.6596 9.85409 14.6596 9.91977 14.6505 9.9844V14.8652H17.2022C17.3791 14.8629 17.553 14.9111 17.7033 15.0043C17.7709 15.046 17.8289 15.1014 17.8737 15.167C17.9184 15.2325 17.9489 15.3067 17.9631 15.3848C17.9631 15.3848 17.9631 15.4776 17.9631 15.524C17.9675 15.6164 17.9496 15.7084 17.9108 15.7924C17.8721 15.8764 17.8136 15.9499 17.7404 16.0065C17.5787 16.1237 17.3834 16.1855 17.1837 16.1828H14.0474C13.8476 16.1956 13.6491 16.1436 13.4813 16.0344C13.3313 15.9222 13.2313 15.7556 13.203 15.5704C13.1962 15.4685 13.1962 15.3661 13.203 15.2641L13.1751 9.9844Z"
      fill="#FEE500"
    />
    <path
      d="M18.1304 9.98472C18.1249 9.77708 18.1978 9.575 18.3346 9.41871C18.4736 9.29233 18.652 9.21771 18.8396 9.20757C19.0273 9.19743 19.2127 9.25233 19.3645 9.36298C19.4962 9.47086 19.5851 9.62219 19.6151 9.78978C19.6197 9.85465 19.6197 9.91984 19.6151 9.98472V12.258L21.9627 9.54851C22.0531 9.45608 22.1492 9.36939 22.2503 9.28879C22.3456 9.23203 22.4549 9.20302 22.5658 9.20519C22.728 9.20431 22.8866 9.25288 23.0205 9.3444C23.0836 9.38688 23.1375 9.4417 23.1789 9.50547C23.2204 9.56924 23.2485 9.64068 23.2618 9.71559C23.2655 9.73396 23.2655 9.75295 23.2618 9.77132C23.2573 9.79273 23.2573 9.8148 23.2618 9.83622C23.2614 9.93386 23.2359 10.0298 23.1875 10.1146C23.1383 10.2045 23.0792 10.2884 23.0112 10.3651L21.4709 12.0539L23.3267 14.9304V15.0232C23.4231 15.1562 23.4982 15.3034 23.5494 15.4594V15.5057C23.5601 15.6132 23.5446 15.7218 23.5042 15.822C23.4638 15.9222 23.3997 16.011 23.3174 16.081C23.1626 16.198 22.9733 16.2601 22.7792 16.2574C22.6503 16.2627 22.5223 16.2338 22.4081 16.1738C22.2845 16.0909 22.1827 15.9795 22.1112 15.849L20.3852 13.0653L19.5594 13.9283V15.4779C19.565 15.6855 19.4921 15.8876 19.3553 16.0439C19.2819 16.1155 19.1947 16.1715 19.099 16.2082C19.0032 16.2449 18.901 16.2617 18.7985 16.2574C18.6273 16.261 18.4601 16.2052 18.3253 16.0996C18.1942 15.9951 18.1053 15.8469 18.0747 15.6821C18.0656 15.6144 18.0656 15.5456 18.0747 15.4779L18.1304 9.98472Z"
      fill="#FEE500"
    />
  </svg>
);

export const MyProfileWorkSpaceIcon = () => (
  <svg
    width="60"
    height="60"
    viewBox="0 0 60 60"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <rect width="60" height="60" fill="white" />
    <mask id="path-1-inside-1_1359_39857" fill="white">
      <rect x="10" y="5" width="37" height="50" rx="1" />
    </mask>
    <rect
      x="10"
      y="5"
      width="37"
      height="50"
      rx="1"
      stroke="black"
      strokeWidth="4"
      mask="url(#path-1-inside-1_1359_39857)"
    />
    <mask id="path-2-inside-2_1359_39857" fill="white">
      <rect x="17" y="12" width="9" height="9" rx="1" />
    </mask>
    <rect
      x="17"
      y="12"
      width="9"
      height="9"
      rx="1"
      stroke="black"
      strokeWidth="4"
      mask="url(#path-2-inside-2_1359_39857)"
    />
    <mask id="path-3-inside-3_1359_39857" fill="white">
      <rect x="31" y="12" width="9" height="9" rx="1" />
    </mask>
    <rect
      x="31"
      y="12"
      width="9"
      height="9"
      rx="1"
      stroke="black"
      strokeWidth="4"
      mask="url(#path-3-inside-3_1359_39857)"
    />
    <mask id="path-4-inside-4_1359_39857" fill="white">
      <rect x="17" y="26" width="9" height="9" rx="1" />
    </mask>
    <rect
      x="17"
      y="26"
      width="9"
      height="9"
      rx="1"
      stroke="black"
      strokeWidth="4"
      mask="url(#path-4-inside-4_1359_39857)"
    />
    <path
      d="M30.5 38.5C30.5 45.1274 35.8726 50.5 42.5 50.5C49.1274 50.5 54.5 45.1274 54.5 38.5C54.5 31.8726 49.1274 26.5 42.5 26.5C35.8726 26.5 30.5 31.8726 30.5 38.5Z"
      fill="white"
      stroke="white"
      strokeWidth="3"
      strokeLinejoin="round"
    />
    <path
      d="M42.5 25.5C35.3203 25.5 29.5 31.3203 29.5 38.5C29.5 45.6797 35.3203 51.5 42.5 51.5C49.6797 51.5 55.5 45.6797 55.5 38.5C55.5 31.3203 49.6797 25.5 42.5 25.5ZM34.5 38.5C34.5 34.0817 38.0817 30.5 42.5 30.5C46.9183 30.5 50.5 34.0817 50.5 38.5C50.5 42.9183 46.9183 46.5 42.5 46.5C38.0817 46.5 34.5 42.9183 34.5 38.5Z"
      fill="black"
      stroke="white"
      strokeWidth="3"
      strokeLinejoin="round"
    />
    <rect x="41" y="32" width="3" height="7" fill="black" />
    <rect x="41" y="41" width="3" height="3" fill="black" />
  </svg>
);

export const MyProfileRightArrow = () => (
  <svg
    width="24"
    height="25"
    viewBox="0 0 24 25"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M9 18.9951L15 12.9951L9 6.99512"
      stroke="black"
      strokeWidth="1.75"
      strokeLinecap="square"
      strokeLinejoin="round"
    />
  </svg>
);

export const DefaultImageIcon = () => (
  <svg
    width="80"
    height="80"
    viewBox="0 0 80 80"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <rect
      x="0.5"
      y="0.5"
      width="79"
      height="79"
      rx="2.5"
      fill="white"
      stroke="white"
    />
    <circle cx="40" cy="40" r="21" stroke="black" strokeWidth="3" />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M44.5 33.5C44.5 35.9865 42.4865 38 40 38C37.516 38 35.5 35.9865 35.5 33.5C35.5 31.0135 37.516 29 40 29C42.484 29.0024 44.4976 31.016 44.5 33.5Z"
      stroke="url(#paint0_linear_1379_37362)"
      strokeWidth="3"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M48 50.5H32C32 49.3632 32 48.2817 32 47.3023C32 44.6496 34.149 42.5 36.8 42.5H43.2C45.851 42.5 48 44.6496 48 47.3023C48 48.2817 48 49.3632 48 50.5Z"
      stroke="black"
      strokeWidth="3"
      strokeLinecap="square"
    />
    <path
      d="M59.4 48C58 51.5 56.38 53.5 53.58 56"
      stroke="url(#paint1_linear_1379_37362)"
      strokeWidth="3"
      strokeLinecap="square"
      strokeLinejoin="round"
    />
    <defs>
      <linearGradient
        id="paint0_linear_1379_37362"
        x1="44.5"
        y1="29"
        x2="34.918"
        y2="31.4661"
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor="#E0004D" />
        <stop offset="1" stopColor="#EA27C2" />
      </linearGradient>
      <linearGradient
        id="paint1_linear_1379_37362"
        x1="59.4"
        y1="48"
        x2="53.017"
        y2="49.1952"
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor="#E0004D" />
        <stop offset="1" stopColor="#EA27C2" />
      </linearGradient>
    </defs>
  </svg>
);

export const MyProfileAlertList = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 18 18"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M9.25 0.995117C12.5637 0.995117 15.25 3.68152 15.25 6.99523C15.25 7.70881 15.25 8.34882 15.25 8.81734C15.25 10.9507 17.25 11.6618 17.25 11.6618L1.25 11.6618C1.25 11.6618 3.25 10.9507 3.25 8.81734C3.25 8.34882 3.25 7.70881 3.25 6.99523C3.25 3.68152 5.93629 0.995117 9.25 0.995117V0.995117Z"
      stroke="black"
      strokeWidth="1.25"
      strokeLinejoin="round"
    />
    <path
      d="M5.25 12.9951C5.25 15.2043 7.04086 16.9951 9.25 16.9951C11.4591 16.9951 13.25 15.2043 13.25 12.9951"
      stroke="black"
      strokeWidth="1.25"
    />
  </svg>
);

export const MyProfileHistory = () => (
  <svg
    width="17"
    height="17"
    viewBox="0 0 17 17"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <g clipPath="url(#clip0_1380_38719)">
      <path
        d="M1.75 8.99512C1.75 5.12912 4.88401 1.99512 8.75 1.99512C12.616 1.99512 15.75 5.12912 15.75 8.99512C15.75 12.8611 12.616 15.9951 8.75 15.9951C4.88401 15.9951 1.75 12.8611 1.75 8.99512Z"
        stroke="black"
      />
      <path
        d="M8.75 4.99512V9.02439L11.4362 11.7106"
        stroke="black"
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
      <rect x="0.75" y="1" width="3.25" height="8" fill="white" />
      <path
        d="M4.5 0.995117L4 3.99023L6.5 5.49512"
        stroke="black"
        strokeLinejoin="round"
      />
    </g>
    <defs>
      <clipPath id="clip0_1380_38719">
        <rect
          width="16"
          height="16"
          fill="white"
          transform="translate(0.75 0.995117)"
        />
      </clipPath>
    </defs>
  </svg>
);

export const MyProfileCamera = () => (
  <svg
    width="24"
    height="25"
    viewBox="0 0 24 25"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M15.04 4.49219L16.772 7.19755H21.25L21.25 20.5082H2.75001L2.75 7.19755H7.228L8.95901 4.49219H15.04Z"
      stroke="white"
      strokeWidth="1.5"
      strokeLinecap="square"
    />
    <path
      d="M17.9271 11.0352H17.9361"
      stroke="white"
      strokeWidth="1.5"
      strokeLinecap="square"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M15.1783 13.7142C15.1783 11.9582 13.7553 10.5352 11.9993 10.5352C10.2433 10.5352 8.82031 11.9582 8.82031 13.7142C8.82031 15.4702 10.2433 16.8932 11.9993 16.8932C13.7553 16.8932 15.1783 15.4702 15.1783 13.7142Z"
      stroke="white"
      strokeWidth="1.5"
      strokeLinecap="square"
    />
  </svg>
);
